// Install-agreement payload + staleness detection.
//
// buildAgreementPayload is the single source of truth for the agreement_data
// snapshot shape. It is written into deals.agreement_data when an agreement is
// generated/resubmitted (staff side, ScopeSection) AND read live by the
// homeowner SigningPage for UNSIGNED deals so the on-screen numbers always
// match live scope columns (and therefore the PDF generated at sign time).
//
// agreementIsStale compares a stored snapshot against a freshly computed
// payload — if the scope INPUTS drifted, a snapshot-rendered surface is showing
// old numbers and the agreement needs a resubmit.
//
// Extracted to lib (Jun 24, 2026) from ScopeSection so SigningPage can import
// buildAgreementPayload without dragging the entire staff scope component into
// the homeowner bundle.
//
// Oct 29, 2026 — trades billed to the homeowner (Greg: "the trade sheet is the
// first step; the homeowner agreement picks it up from there"). A
// homeowner-billed trade's customer price reaches the deal row as derived
// columns (SQL trade_sync_deal: trade_ho_total, trade_ho_lines, …) that
// calcScope reads. tradeMoneyOf() below is the ONE place every surface (the
// agreement PDF, the signing page, the payment schedule, the Scope card, the
// portals) reads them, so the customer lines, the "due at completion" amount
// and the staff-only floor / upside never disagree between surfaces. The
// snapshot carries the customer side only (trade_ho_total, trade_lines,
// completion_payment): the homeowner reads agreement_data, and never sees the
// floor or the upside split, let alone the sub's price.
// ---------------------------------------------------------------------------

import type { Deal, RetailLineItem } from "./types";
import { calcScope, type ScopeCalc } from "./scopeCalc";
import { picksSignature, readPicks, type PickedLine } from "./scopeLines";

// ── Trades billed to the homeowner (Oct 29, 2026) ─────────────────────────

/** One customer-facing trade line ("Gutters: detach and reset, 199 ft",
 *  $1,000): what the agreement, the signing page, the payment schedule and the
 *  invoice print. Never a sub figure, never the floor. */
export interface AgreementTradeLine {
  label: string;
  amount: number;
}

/** calcScope's trade outputs (contract D15, migration 20261029f). Read through
 *  tradeMoneyOf() only, so a ScopeCalc without them (a build before the money
 *  model lands) reads as "no trades" instead of failing. */
type TradeCalcFields = {
  tradeHoTotal: number;
  tradeHoFloor: number;
  tradeUpside: number;
  tradeMarginLoss: number;
  tradeHoLines: ReadonlyArray<unknown>;
  completionPayment: number;
};

export interface TradeMoney {
  /** Σ customer price of the live homeowner-billed trades (deals.trade_ho_total). */
  total: number;
  /** The customer lines, in the order the trades were made. A total with no
   *  readable lines prints as one "Other trades" line so the sums still add. */
  lines: AgreementTradeLine[];
  /** Staff only: Σ min(price, the 30% floor) — at cost to the commission formula. */
  floor: number;
  /** Staff only: max(0, total − floor), the pool paid as commission. */
  upside: number;
  /** Staff only: the trade price shortfalls Chad deducted (they join the non-collect bucket). */
  marginLoss: number;
  /** What is due after the build: the final balance basis (calcScope
   *  completionPayment; before the money model lands, initial invoice − first payment). */
  completionPayment: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const numOr0 = (v: unknown) => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Fallback label for a trade line that has none (a price row written before
 *  the customer label existed). */
export const TRADE_LINE_FALLBACK_LABEL = "Trade work";

/** Trade lines as calcScope hands them (tradeHoLines) or as a snapshot stores
 *  them (agreement_data.trade_lines), read defensively: an old snapshot has
 *  none, a line with no label gets the fallback, a $0 line is left out. */
export function readTradeLines(v: unknown): AgreementTradeLine[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((l): l is Record<string, unknown> => !!l && typeof l === "object")
    .map((l) => ({
      label: String(l.label ?? "").replace(/\s+/g, " ").trim() || TRADE_LINE_FALLBACK_LABEL,
      amount: round2(numOr0(l.amount)),
    }))
    .filter((l) => l.amount !== 0);
}

/** The deal's homeowner-billed trades, read from a calcScope result. */
export function tradeMoneyOf(calc: ScopeCalc): TradeMoney {
  const c = calc as ScopeCalc & Partial<TradeCalcFields>;
  const total = round2(Math.max(0, numOr0(c.tradeHoTotal)));
  const floor = round2(Math.max(0, Math.min(total, numOr0(c.tradeHoFloor))));
  const lines = total > 0 ? readTradeLines(c.tradeHoLines) : [];
  return {
    total,
    lines: lines.length === 0 && total > 0 ? [{ label: "Other trades", amount: total }] : lines,
    floor,
    upside: total > 0 ? round2(Math.max(0, numOr0(c.tradeUpside ?? total - floor))) : 0,
    marginLoss: round2(Math.max(0, numOr0(c.tradeMarginLoss))),
    completionPayment: typeof c.completionPayment === "number" && Number.isFinite(c.completionPayment)
      ? round2(c.completionPayment)
      : round2(Math.max(0, calc.initialInvoice - calc.firstPaymentToBardin)),
  };
}

/** Where the after-build card takes a credit beyond the deductible (Oct 30, 2026, review of the trade release): off
 *  the trades billed to the homeowner first, never below $0, and what is left off the insurance portion (never below
 *  $0 either), so the two subtotals add up to the completion payment instead of the trades going negative. */
export function afterBuildCredit(creditOverage: number, tradesTotal: number, insurancePortion: number): { onTrades: number; onInsurance: number } {
  const over = round2(Math.max(0, numOr0(creditOverage)));
  const onTrades = round2(Math.min(over, Math.max(0, numOr0(tradesTotal))));
  const onInsurance = round2(Math.min(over - onTrades, Math.max(0, numOr0(insurancePortion))));
  return { onTrades, onInsurance };
}

export function buildAgreementPayload(deal: Deal) {
  const fullCalc = calcScope(deal);
  const trades = tradeMoneyOf(fullCalc);
  return {
    source: "scope_card_generate",
    homeowner_name: deal.homeowner_name,
    property_address: [deal.property_street, deal.property_city, deal.property_state, deal.property_zip]
      .filter(Boolean)
      .join(", "),
    claim_number: deal.claim_number,
    insurance_company: deal.insurance_company,
    total_rcv: fullCalc.rcv,
    total_acv: fullCalc.acv,
    total_depreciation: fullCalc.dep,
    deductible: fullCalc.deductible,
    supplements: fullCalc.supplements,
    pwi_code: fullCalc.pwi,
    upgrades: fullCalc.upgrades,
    retail_profit: fullCalc.retailProfit,
    at_cost_retail: fullCalc.atCostRetail,
    acv_credit: fullCalc.acvCredit,
    dep_credit: fullCalc.depCredit,
    misc_credit: fullCalc.miscCredit,
    non_commissionable: fullCalc.nonCommissionable,
    non_collect_revenue: fullCalc.nonCollectRevenue,
    // non_collect_overage and commissionable_revenue are no longer in the
    // snapshot (Oct 30, 2026, review of the trade release): ho_resolve_deal and
    // ho_sign_agreement hand agreement_data to the homeowner's browser, and since
    // the trade money they carry the shortfalls Chad deducted and (retail) the
    // upside pool, which 20261029f strips from the deal row. Nothing reads them
    // from the snapshot (agreementIsStale compares inputs only).
    insurance_pays: fullCalc.insurancePays,
    acv_settlement: fullCalc.acvSettlement,
    acv_settlement_to_bardin: fullCalc.acvSettlementToBardin,
    first_payment_to_bardin: fullCalc.firstPaymentToBardin,
    depreciation_settlement: fullCalc.depSettlement,
    acv_only: fullCalc.acvOnly,
    ho_out_of_pocket: fullCalc.hoOutOfPocket,
    initial_invoice: fullCalc.initialInvoice,
    credits_applied: fullCalc.creditsApplied,
    credit_overage: fullCalc.creditOverage,
    items_not_performing_lines: deal.items_not_performing_lines || null,
    // Oct 30, 2026 (scope gate) — the carrier lines Bardin won't do, as the RM
    // ticked them: the signing page and the PDF list them under "Work Bardin
    // won't do" with their ACV. Retail jobs have no carrier lines.
    items_not_performing_detail: fullCalc.isRetail ? null : snapshotPicks(deal.items_not_performing_detail),
    // Oct 2, 2026 — customer-facing add-on descriptions (internal notes are
    // deliberately NOT in the snapshot; the homeowner reads agreement_data).
    upgrades_description: deal.upgrades_description?.trim() || null,
    retail_profit_description: deal.retail_profit_description?.trim() || null,
    at_cost_retail_description: deal.at_cost_retail_description?.trim() || null,
    // ── Retail option (Jun 11, 2026) ──────────────────────────────────────
    // When is_retail, the signing page + PDF render the 50/50 retail schedule
    // from these instead of the insurance two-settlement breakdown.
    is_retail: fullCalc.isRetail,
    retail_contract_total: fullCalc.retailContractTotal,
    retail_squares: fullCalc.retailSquares,
    retail_base_per_square: fullCalc.retailBasePerSquare,
    retail_deposit: fullCalc.retailDeposit,
    retail_completion_payment: fullCalc.retailCompletionPayment,
    retail_scope_description: (deal as { retail_scope_description?: string | null }).retail_scope_description || null,
    retail_items: (deal as { retail_items?: RetailLineItem[] | null }).retail_items || null,
    retail_at_cost_total: fullCalc.retailAtCostTotal,
    // ── Trades billed to the homeowner (Oct 29, 2026) ─────────────────────
    // Customer side only: the price per line and the total, due at completion
    // (never in the first payment). The floor, the upside and the sub's price
    // stay off the snapshot — the homeowner reads agreement_data.
    trade_ho_total: trades.total,
    trade_lines: trades.lines,
    completion_payment: trades.completionPayment,
    generated_at: new Date().toISOString(),
  };
}

/** The picks as the snapshot keeps them (the read they came from stays on the deal). */
function snapshotPicks(v: unknown): Omit<PickedLine, "read_id">[] | null {
  const picks = readPicks(v);
  if (picks.length === 0) return null;
  return picks.map(({ read_id: _readId, ...rest }) => rest);
}

/** Order-free signature of a snapshot's (or the deal's) picked lines. A snapshot
 *  taken before Oct 30, 2026 has none and equals a deal with no picks. */
function picksSnapshotSignature(v: unknown): string {
  return readPicks(v)
    .map((p) => picksSignature([p]))
    .sort()
    .join("\n");
}

// Scope INPUT fields compared snapshot-vs-live. Derived fields (initial
// invoice, settlements, …) follow deterministically from these, so comparing
// inputs is sufficient and avoids false positives from formula corrections
// shipped after a snapshot was taken.
const AGREEMENT_STALE_NUMERIC_KEYS = [
  "total_rcv",
  "total_depreciation",
  "deductible",
  "supplements",
  "pwi_code",
  "upgrades",
  "retail_profit",
  "at_cost_retail",
  "acv_credit",
  "dep_credit",
  "misc_credit",
  "non_commissionable",
  // Oct 29, 2026: a homeowner-billed trade saved (or repriced, or called off)
  // on the trade sheet after the agreement was generated changes what the
  // homeowner signs — the Resubmit banner shows and Chad re-approves. Its
  // lines are compared by signature in agreementIsStale.
  "trade_ho_total",
] as const;

// Stages where the install agreement is no longer the live ask (build done /
// supps lifecycle / terminal). Scope edits here are routine finance updates
// (Shannon's supplements, non-collect, misc credit) — NOT agreement revisions.
// The stale banner is suppressed for deals at these stages, and the SigningPage
// keeps rendering the frozen snapshot rather than live numbers.
export const POST_AGREEMENT_STAGES = new Set([
  "ready_for_supps", "ready_for_updated_invoice", "supps_waiting_on_claims",
  "supps_waiting_on_final_invoice", "collecting", "delinquent",
  "register_oc_warranty", "roof_complete", "closed_won", "closed_lost", "cancelled",
]);

/** The keys of AGREEMENT_STALE_NUMERIC_KEYS that belong to the trades billed to
 *  the homeowner (the rest are the roof scope). */
const TRADE_STALE_KEYS: ReadonlySet<string> = new Set(["trade_ho_total"]);

/** Normalized signature of a snapshot's trade lines (label·amount in cents),
 *  so a relabel, a reprice or a called-off trade all count as a change. A
 *  snapshot taken before Oct 29, 2026 has no trade_lines: it reads "" and
 *  equals a deal with no trades — no false positive. */
function tradeLinesSignature(v: unknown): string {
  return readTradeLines(v)
    .map((l) => `${l.label}·${Math.round(l.amount * 100)}`)
    .join("|");
}

/** Which part of the agreement drifted from the live deal: the trades billed to
 *  the homeowner (a trade-sheet change), the rest of the scope, or both. null
 *  when there is no snapshot. The Scope card words its Resubmit banner from it. */
export function agreementStaleParts(deal: Deal): { scope: boolean; trades: boolean } | null {
  const snap = deal.agreement_data as Record<string, unknown> | null | undefined;
  if (!snap || typeof snap !== "object") return null;
  const fresh = buildAgreementPayload(deal) as unknown as Record<string, unknown>;
  // Round to cents — snapshots store JS floats (e.g. 996.3499999999999).
  const cents = (v: unknown) => Math.round((Number(v) || 0) * 100);
  let scope = false;
  let trades = false;
  for (const k of AGREEMENT_STALE_NUMERIC_KEYS) {
    if (cents(snap[k]) === cents(fresh[k])) continue;
    if (TRADE_STALE_KEYS.has(k)) trades = true;
    else scope = true;
  }
  if (tradeLinesSignature(snap.trade_lines) !== tradeLinesSignature(fresh.trade_lines)) trades = true;
  return { scope: scope || otherScopeInputsDrifted(snap, fresh), trades };
}

export function agreementIsStale(deal: Deal): boolean {
  const parts = agreementStaleParts(deal);
  return !!parts && (parts.scope || parts.trades);
}

/** The non-numeric scope inputs: flags, retail inputs, descriptions. */
function otherScopeInputsDrifted(snap: Record<string, unknown>, fresh: Record<string, unknown>): boolean {
  const cents = (v: unknown) => Math.round((Number(v) || 0) * 100);
  if ((snap.acv_only === true) !== (fresh.acv_only === true)) return true;
  // Retail inputs (Jun 11, 2026) — a flag flip or contract/squares change after
  // generation means the signing link shows stale numbers, same as insurance.
  if ((snap.is_retail === true) !== (fresh.is_retail === true)) return true;
  if (fresh.is_retail === true) {
    if (cents(snap.retail_contract_total) !== cents(fresh.retail_contract_total)) return true;
    if (cents(snap.retail_squares) !== cents(fresh.retail_squares)) return true;
    if (cents(snap.retail_at_cost_total) !== cents(fresh.retail_at_cost_total)) return true;
    // Compare the line-item set by a normalized signature (label·kind·amount).
    const sig = (v: unknown) =>
      Array.isArray(v)
        ? (v as Array<Record<string, unknown>>)
            .map((it) => `${String(it.label ?? "").trim()}·${it.kind}·${Math.round((Number(it.amount) || 0) * 100)}`)
            .join("|")
        : "";
    if (sig(snap.retail_items) !== sig(fresh.retail_items)) return true;
  }
  const str = (v: unknown) => (v == null ? "" : String(v).trim());
  if (str(snap.items_not_performing_lines) !== str(fresh.items_not_performing_lines)) return true;
  // The lines Bardin won't do are contract text too (Oct 30, 2026).
  if (picksSnapshotSignature(snap.items_not_performing_detail) !== picksSnapshotSignature(fresh.items_not_performing_detail)) return true;
  // Add-on descriptions are contract text the homeowner signs — a change means
  // the signing link is stale. (Pre-Oct-2 snapshots lack the keys; an empty
  // live description compares equal to the missing key, so no false positive.)
  for (const k of ["upgrades_description", "retail_profit_description", "at_cost_retail_description"]) {
    if (str(snap[k]) !== str(fresh[k])) return true;
  }
  if (fresh.is_retail === true && str(snap.retail_scope_description) !== str(fresh.retail_scope_description)) return true;
  return false;
}
